import React from 'react';

import {
  Chip,
  ChipProps,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';

import CheckCircleOutlineIcon
  from '@mui/icons-material/CheckCircleOutline';

import ErrorOutlineIcon
  from '@mui/icons-material/ErrorOutline';

import WarningAmberOutlinedIcon
  from '@mui/icons-material/WarningAmberOutlined';

import HourglassEmptyOutlinedIcon
  from '@mui/icons-material/HourglassEmptyOutlined';

import DraftsOutlinedIcon
  from '@mui/icons-material/DraftsOutlined';

import PublishOutlinedIcon
  from '@mui/icons-material/PublishOutlined';

import CancelOutlinedIcon
  from '@mui/icons-material/CancelOutlined';

import UndoOutlinedIcon
  from '@mui/icons-material/UndoOutlined';

import InfoOutlinedIcon
  from '@mui/icons-material/InfoOutlined';

import HelpOutlineOutlinedIcon
  from '@mui/icons-material/HelpOutlineOutlined';

import {
  BieFlag,
  BieLoadSessionStateCode,
  BieOnboardingValidationStatus,
  BieValidationSeverityCode,
  BieValidationStateCode,
  getBieLoadSessionStateLabel,
  getBieValidationStatusLabel,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';


/* ============================================================
 * Types
 * ============================================================
 */

export type BieOnboardingStatusChipKind =
  | 'LOAD_SESSION'
  | 'VALIDATION'
  | 'VALIDATION_STATE'
  | 'SEVERITY'
  | 'READINESS';

export interface IBieOnboardingStatusChipProps {
  kind: BieOnboardingStatusChipKind;

  /**
   * State, status, severity code or readiness flag.
   */
  value:
  | string
  | number
  | boolean
  | null
  | undefined;

  /**
   * Optional display label overriding the standard label.
   */
  label?: string;

  /**
   * Used primarily with SEVERITY chips.
   */
  blocking?: BieFlag | boolean | null;

  /**
   * Optional Tooltip text.
   */
  tooltip?: string;

  size?: ChipProps['size'];

  //   text.
  //    */
  // tooltip ?: string;

  // size ?: Chip 

  variant ?: ChipProps['variant'];

  clickable ?: boolean;

  disabled ?: boolean;

  onClick ?: () => void;

  sx ?: SxProps<Theme>;
}


/* ============================================================
 * Internal presentation model
 * ============================================================
 */

interface IBieStatusPresentation {
  label: string;

  color: ChipProps['color'];

  icon: React.ReactElement;

  tooltip: string;
}


/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Provides consistent status presentation throughout the BIE
 * Institutional Onboarding Workbench.
 *
 * Supported status categories:
 *
 * - Load Session workflow state
 * - Validation summary status
 * - Import Item validation state
 * - Validation issue severity
 * - Import or Load Session readiness
 */
export default class BieOnboardingStatusChip
  extends React.PureComponent<
    IBieOnboardingStatusChipProps
  > {

  public render(): React.ReactNode {
    const {
      size = 'small',
      variant = 'outlined',
      clickable = false,
      disabled = false,
      onClick,
      sx,
      tooltip,
    } = this.props;

    const presentation =
      this.getPresentation();

    const chip = (
      <Chip
        size={size}
        variant={variant}
        color={presentation.color}
        icon={presentation.icon}
        label={
          this.props.label ||
          presentation.label
        }
        clickable={clickable}
        disabled={disabled}
        onClick={onClick}
        sx={{
          fontWeight: 600,

          '& .MuiChip-icon': {
            fontSize: 18,
          },

          ...sx,
        }}
      />
    );

    const resolvedTooltip =
      tooltip ||
      presentation.tooltip;

    if (!resolvedTooltip) {
      return chip;
    }

    return (
      <Tooltip
        title={resolvedTooltip}
        arrow
      >
        <span>
          {chip}
        </span>
      </Tooltip>
    );
  }


  /* ==========================================================
   * Presentation resolver
   * ==========================================================
   */

  private getPresentation():
    IBieStatusPresentation {
    switch (this.props.kind) {
      case 'LOAD_SESSION':
        return this.getLoadSessionPresentation();

      case 'VALIDATION':
        return this.getValidationPresentation();

      case 'VALIDATION_STATE':
        return this.getValidationStatePresentation();

      case 'SEVERITY':
        return this.getSeverityPresentation();

      case 'READINESS':
        return this.getReadinessPresentation();

      default:
        return this.getUnknownPresentation();
    }
  }


  /* ==========================================================
   * Load Session state
   * ==========================================================
   */

  private getLoadSessionPresentation():
    IBieStatusPresentation {
    const stateCode =
      this.normalizeCode(
        this.props.value,
      ) as BieLoadSessionStateCode;

    const label =
      getBieLoadSessionStateLabel(
        stateCode,
      );

    switch (stateCode) {
      case 'DRAFT':
        return {
          label,
          color: 'default',
          icon: <DraftsOutlinedIcon />,
          tooltip:
            'The Load Session is still being prepared.',
        };

      case 'STAGED':
        return {
          label,
          color: 'info',
          icon: <HourglassEmptyOutlinedIcon />,
          tooltip:
            'The workbook has been staged and is ready for validation.',
        };

      case 'VALIDATED':
        return {
          label,
          color: 'success',
          icon: <CheckCircleOutlineIcon />,
          tooltip:
            'The Load Session passed validation and is ready for publication.',
        };

      case 'REJECTED':
        return {
          label,
          color: 'error',
          icon: <ErrorOutlineIcon />,
          tooltip:
            'Blocking validation issues must be corrected before publication.',
        };

      case 'PUBLISHED':
        return {
          label,
          color: 'primary',
          icon: <PublishOutlinedIcon />,
          tooltip:
            'The validated onboarding data has been published.',
        };

      case 'CANCELLED':
        return {
          label,
          color: 'default',
          icon: <CancelOutlinedIcon />,
          tooltip:
            'The Load Session has been cancelled.',
        };

      case 'ROLLEDBACK':
        return {
          label,
          color: 'warning',
          icon: <UndoOutlinedIcon />,
          tooltip:
            'The published Load Session has been rolled back.',
        };

      default:
        return {
          label,
          color: 'default',
          icon: <HelpOutlineOutlinedIcon />,
          tooltip:
            'The Load Session is in an unknown workflow state.',
        };
    }
  }


  /* ==========================================================
   * Validation summary status
   * ==========================================================
   */

  private getValidationPresentation():
    IBieStatusPresentation {
    const validationStatus =
      this.normalizeCode(
        this.props.value,
      ) as BieOnboardingValidationStatus;

    const label =
      getBieValidationStatusLabel(
        validationStatus,
      );

    switch (validationStatus) {
      case 'VALID':
        return {
          label,
          color: 'success',
          icon: <CheckCircleOutlineIcon />,
          tooltip:
            'All active onboarding records passed validation.',
        };

      case 'WARNING':
        return {
          label,
          color: 'warning',
          icon: <WarningAmberOutlinedIcon />,
          tooltip:
            'Validation completed with non-blocking warnings.',
        };

      case 'FAILED':
        return {
          label,
          color: 'error',
          icon: <ErrorOutlineIcon />,
          tooltip:
            'One or more blocking validation issues were detected.',
        };

      case 'PENDING':
        return {
          label,
          color: 'info',
          icon: <HourglassEmptyOutlinedIcon />,
          tooltip:
            'Validation is pending or reference resolution is incomplete.',
        };

      case 'EMPTY':
        return {
          label,
          color: 'default',
          icon: <InfoOutlinedIcon />,
          tooltip:
            'No active onboarding records are available for validation.',
        };

      default:
        return {
          label,
          color: 'default',
          icon: <HelpOutlineOutlinedIcon />,
          tooltip:
            'The validation result is unknown.',
        };
    }
  }


  /* ==========================================================
   * Import Item validation state
   * ==========================================================
   */

  private getValidationStatePresentation():
    IBieStatusPresentation {
    const validationState =
      this.normalizeCode(
        this.props.value,
      ) as BieValidationStateCode;

    switch (validationState) {
      case 'VALID':
        return {
          label: 'Valid',
          color: 'success',
          icon: <CheckCircleOutlineIcon />,
          tooltip:
            'The staged record passed all applicable validation rules.',
        };

      case 'WARNING':
        return {
          label: 'Warning',
          color: 'warning',
          icon: <WarningAmberOutlinedIcon />,
          tooltip:
            'The staged record has one or more non-blocking warnings.',
        };

      case 'FAILED':
        return {
          label: 'Failed',
          color: 'error',
          icon: <ErrorOutlineIcon />,
          tooltip:
            'The staged record has one or more blocking validation issues.',
        };

      case 'PENDING':
        return {
          label: 'Pending',
          color: 'info',
          icon: <HourglassEmptyOutlinedIcon />,
          tooltip:
            'The staged record has not yet completed validation.',
        };

      default:
        return {
          label:
            validationState ||
            'Unknown',

          color: 'default',
          icon: <HelpOutlineOutlinedIcon />,
          tooltip:
            'The staged record has an unknown validation state.',
        };
    }
  }


  /* ==========================================================
   * Validation issue severity
   * ==========================================================
   */

  private getSeverityPresentation():
    IBieStatusPresentation {
    const severityCode =
      this.normalizeCode(
        this.props.value,
      ) as BieValidationSeverityCode;

    const isBlocking =
      isBieFlagEnabled(
        this.props.blocking,
      );

    switch (severityCode) {
      case 'ERROR':
        return {
          label:
            isBlocking
              ? 'Blocking Error'
              : 'Error',

          color: 'error',

          icon: <ErrorOutlineIcon />,

          tooltip:
            isBlocking
              ? 'This issue blocks validation and publication.'
              : 'This validation issue requires correction.',
        };

      case 'WARNING':
        return {
          label:
            isBlocking
              ? 'Blocking Warning'
              : 'Warning',

          color:
            isBlocking
              ? 'error'
              : 'warning',

          icon: <WarningAmberOutlinedIcon />,

          tooltip:
            isBlocking
              ? 'This warning has been configured as a blocking issue.'
              : 'This issue should be reviewed but does not block publication.',
        };

      case 'INFO':
        return {
          label: 'Information',
          color: 'info',
          icon: <InfoOutlinedIcon />,
          tooltip:
            'This issue is informational and does not block publication.',
        };

      default:
        return {
          label:
            severityCode ||
            'Unknown',

          color: 'default',

          icon: <HelpOutlineOutlinedIcon />,

          tooltip:
            'The validation issue has an unknown severity.',
        };
    }
  }


  /* ==========================================================
   * Readiness
   * ==========================================================
   */

  private getReadinessPresentation():
    IBieStatusPresentation {
    const ready =
      isBieFlagEnabled(
        this.props.value,
      );

    if (ready) {
      return {
        label: 'Ready',
        color: 'success',
        icon: <CheckCircleOutlineIcon />,
        tooltip:
          'All requirements have been satisfied for the next workflow action.',
      };
    }

    return {
      label: 'Not Ready',
      color: 'default',
      icon: <HourglassEmptyOutlinedIcon />,
      tooltip:
        'Outstanding validation or resolution requirements remain.',
    };
  }


  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private getUnknownPresentation():
    IBieStatusPresentation {
    return {
      label: 'Unknown',
      color: 'default',
      icon: <HelpOutlineOutlinedIcon />,
      tooltip:
        'The status category is not recognized.',
    };
  }


  private normalizeCode(
    value: unknown,
  ): string {
    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    return String(value)
      .trim()
      .toUpperCase();
  }
}